import { formatMoney } from 'accounting'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'

import { faCog, faShare, faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, IconWithMenu, Link, ListItem, StatusBadge, Text,
} from '@domain-group/suit-theme'

import useFinanceApplication, { UseFinanceApplicationPayload } from '@hooks/useFinanceApplication'

import { confirmDelete } from '@functions/entity'
import { statusColors } from '@functions/status'

import type { FinanceApplicationModel } from '@models/types'

const generateFinanceApplicationUrl = (
  generateUrlFn: UseFinanceApplicationPayload['callbacks']['generateFinanceApplicationUrl'],
) => {
  generateUrlFn().then((response) => {
    // @ts-expect-error TS2339
    const { success, errors, redirectUrl } = response

    if (!success){
      toast.warn(errors[0])
      return
    }

    if (!redirectUrl){
      toast.warn('Did not receive an external url.')
      return
    }

    window.open(redirectUrl)
  })
}

const FinanceApplicationListItem = (props: { financeApplication: FinanceApplicationModel }) => {
  const { financeApplication } = props

  const financeApplicationPayload = useFinanceApplication(financeApplication)
  const {
    applicationStatus,
    callbacks: {
      deleteFinanceApplication: deleteFn,
      generateFinanceApplicationUrl: generateUrlFn,
    },
    financeApplication: {
      approved, dates, id, total,
    },
    paymentMethod,
  } = financeApplicationPayload

  const externalId = digObject(financeApplication, 'data.external_id')
  const statusKey = applicationStatus.toLowerCase()

  // @ts-expect-error TS2345
  const createdDate = digObject(dates, 'created.date_time', '')

  const createdDateFormatted = DateTime.fromFormat(createdDate, 'dd/MM/yyyy HH:mm').toFormat('dd/MM/yyyy')

  const confirmDeletePayload = {
    callbacks: { deleteFn },
    strings: {
      confirmString: 'Are you sure? Caution is advised as this will cancel an external finance application and delete it from Realhub.',
      entityName: 'Finance Application',
    },
  }

  // @ts-expect-error TS7053
  const externalIdStatusColour = externalId ? statusColors[statusKey] : 'red'

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        borderBottom: '1px dashed',
        padding: 'large',
      }}
      disableHover
    >
      <Box flexDirection="column">
        <Text fontSize="small">
          {/*
           // @ts-expect-error TS2339 */}
          {paymentMethod?.title || `Finance Application #${id}`}
        </Text>

        <StatusBadge
          boxProps={{ marginTop: 'medium' }}
          color={approved && !externalId ? 'green' : externalIdStatusColour}
          ghost
          text={applicationStatus}
        />
      </Box>

      <Box
        alignItems="flex-end"
        flexDirection="column"
        justifyContent="center"
        marginLeft="auto"
        marginRight="large"
        width="auto"
      >
        <Text fontSize="small" style={{ whiteSpace: 'nowrap' }}>
          {/*
           // @ts-expect-error TS2769 */}
          {formatMoney(total)}
        </Text>

        <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium" style={{ whiteSpace: 'nowrap' }}>
          Created: {createdDateFormatted}
        </Text>
      </Box>

      <IconWithMenu
        icon={(
          <Link greyLink>
            <FontAwesomeIcon icon={faCog} />
          </Link>
        )}
      >
        {/*
         // @ts-expect-error TS2741 */}
        <IconWithMenu.Menu closeOnClick>
          {!!externalId && (
            <Link onClick={() => generateFinanceApplicationUrl(generateUrlFn)} key="view">
              <FontAwesomeIcon icon={faShare} /> View Application
            </Link>
          )}

          <Link onClick={() => confirmDelete(confirmDeletePayload)} key="delete">
            <FontAwesomeIcon icon={faTrashAlt} /> Delete Application
          </Link>
        </IconWithMenu.Menu>
      </IconWithMenu>
    </ListItem>
  )
}

export default FinanceApplicationListItem
